/**
 * Widget state management utilities
 */

export function createDefaultWidgetState() {
  return {
    modelContent: {
      savedListingIds: [],
      savedProjectIds: [],
      searchHistory: [],
    },
    privateContent: {
      viewMode: "list",
      selectedListingId: null,
    },
    imageIds: [],
  };
}

export function normalizeWidgetState(raw) {
  const base = raw && typeof raw === "object" ? raw : {};
  const modelContent =
    base.modelContent && typeof base.modelContent === "object" ? base.modelContent : {};
  const privateContent =
    base.privateContent && typeof base.privateContent === "object" ? base.privateContent : {};

  const savedListingIds = (
    Array.isArray(modelContent.savedListingIds)
      ? modelContent.savedListingIds
      : Array.isArray(privateContent.savedListingIds)
        ? privateContent.savedListingIds
        : []
  ).filter((id) => typeof id === "string" && id.length > 0);

  const savedProjectIds = (
    Array.isArray(modelContent.savedProjectIds)
      ? modelContent.savedProjectIds
      : Array.isArray(privateContent.savedProjectIds)
        ? privateContent.savedProjectIds
        : []
  ).filter((id) => typeof id === "string" && id.length > 0);

  const searchHistory = (Array.isArray(modelContent.searchHistory) ? modelContent.searchHistory : [])
    .filter((entry) => entry && typeof entry === "object")
    .map((entry) => ({
      path: typeof entry.path === "string" ? entry.path : null,
      filters: typeof entry.filters === "string" ? entry.filters : null,
      at: typeof entry.at === "string" ? entry.at : null,
    }))
    .filter((entry) => entry.path || entry.filters)
    .slice(0, 10);

  const viewMode = privateContent.viewMode === "map" ? "map" : "list";
  const selectedListingId =
    typeof privateContent.selectedListingId === "number" &&
    Number.isFinite(privateContent.selectedListingId)
      ? privateContent.selectedListingId
      : null;

  return {
    modelContent: {
      savedListingIds,
      savedProjectIds,
      searchHistory,
    },
    privateContent: {
      viewMode,
      selectedListingId,
    },
    imageIds: Array.isArray(base.imageIds) ? base.imageIds : [],
  };
}

/**
 * Normalize tool response to extract structured content.
 *
 * Two data shapes:
 * 1. toolOutput (from host) -> structuredContent directly, so x.kind works
 *    _meta comes from window.openai.toolResponseMetadata separately
 * 2. callTool result -> { structuredContent, content, _meta }
 *
 * @param {Object} toolResult - Tool output or callTool response
 * @param {Object} [metaOverride] - Optional _meta from toolResponseMetadata (for host globals)
 * @returns {Object|null} Normalized structured content with _meta merged
 */
export function extractStructuredContent(toolResult, metaOverride) {
  if (!toolResult) return null;

  // callTool returns { structuredContent, meta, content }
  // Note: Apps SDK returns _meta as "meta" (without underscore)
  // toolOutput is structuredContent directly
  const structured = toolResult.structuredContent ?? toolResult;
  const meta = toolResult.meta ?? toolResult._meta ?? metaOverride ?? {};

  // Merge structured with _meta for widget-only data (listing, listings, etc.)
  if (structured && typeof structured === 'object') {
    return {
      ...structured,
      ...meta,
    };
  }

  return null;
}
